/**
 * Autosave — the safety net so a tired player never loses progress.
 *
 * Centralizes ALL "save without being asked" behavior in one place (driven from
 * scenes/session.ts setSession), so the feature scenes never own persistence
 * lifecycle. Two complementary guarantees:
 *
 *  (a) PERIODIC. A gentle ~15s heartbeat calls the live session's debounced save()
 *      while a session is active, so even a hard crash loses at most a few seconds.
 *  (b) ON HIDE / CLOSE. visibilitychange→hidden and pagehide/beforeunload call the
 *      session's flush() — write the IndexedDB cache + fire the server put NOW (no
 *      debounce) — so closing the tab or backgrounding the phone always saves.
 *
 * Hard guarantees (CLAUDE.md): saving is non-blocking and NEVER throws into the page;
 * a storage / network hiccup is swallowed (losing one autosave is fine, a crash is
 * not). Phaser reuses scene instances, so the start/stop here is idempotent and fully
 * tears down its timer + every listener on stop — no stacked intervals, no leaked
 * handlers across a New Game / Continue / scene re-entry.
 */

/** The minimal slice of a GameSession the autosave drives (keeps it test-friendly). */
export interface AutosaveTarget {
  /** Debounced best-effort save (the periodic heartbeat). Must never throw. */
  save(): Promise<void>;
  /** Immediate durable flush for tab hide/close (IndexedDB + server put). Never throws. */
  flush(): Promise<void>;
}

/** Injectable host environment — defaults to the browser globals, stubbed in tests. */
export interface AutosaveHost {
  setInterval: (handler: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
  /** The event target for visibility/close hooks (window in the browser). */
  addEventListener?: (type: string, handler: () => void) => void;
  removeEventListener?: (type: string, handler: () => void) => void;
  /** Read the page visibility state (document.visibilityState in the browser). */
  visibilityState?: () => string;
}

export interface AutosaveOptions {
  /** Heartbeat period; defaults to {@link DEFAULT_AUTOSAVE_INTERVAL_MS}. */
  readonly intervalMs?: number;
  /** Host override (tests inject a fake; prod uses the real globals). */
  readonly host?: AutosaveHost;
}

/** ~15s: frequent enough that a crash loses almost nothing, calm enough to be free. */
export const DEFAULT_AUTOSAVE_INTERVAL_MS = 15_000;

/** Page lifecycle events that mean "we might be torn down — flush durably now". */
const HIDE_EVENTS = ['pagehide', 'beforeunload'] as const;

/**
 * A running autosave loop bound to one session. {@link start} wires the heartbeat +
 * hide/close hooks; {@link stop} tears every one of them down (idempotent). Saving is
 * fire-and-forget and swallows all errors, so a tick or a close hook can never throw
 * into the timer / event loop.
 */
export class Autosave {
  private readonly target: AutosaveTarget;
  private readonly intervalMs: number;
  private readonly host: AutosaveHost;

  private intervalHandle: unknown = null;
  private readonly tick = (): void => void this.target.save().catch(() => {});
  private readonly onHide = (): void => void this.target.flush().catch(() => {});
  private readonly onVisibility = (): void => {
    // Only flush when the page is actually going hidden (not on becoming visible).
    const state = this.host.visibilityState?.();
    if (state === undefined || state === 'hidden') this.onHide();
  };
  private listening = false;

  constructor(target: AutosaveTarget, options: AutosaveOptions = {}) {
    this.target = target;
    this.intervalMs = options.intervalMs ?? DEFAULT_AUTOSAVE_INTERVAL_MS;
    this.host = options.host ?? defaultHost();
  }

  /** Begin the heartbeat + hide/close hooks. Idempotent (a second call is a no-op). */
  start(): void {
    if (this.intervalHandle != null || this.listening) return; // already running
    this.intervalHandle = this.host.setInterval(this.tick, this.intervalMs);

    const add = this.host.addEventListener?.bind(this.host);
    if (add) {
      add('visibilitychange', this.onVisibility);
      for (const evt of HIDE_EVENTS) add(evt, this.onHide);
      this.listening = true;
    }
  }

  /**
   * Stop the heartbeat and remove EVERY listener (the Phaser-reuse safety: no stacked
   * intervals, no leaked handlers). Idempotent. Does NOT itself save — the caller
   * flushes when it wants a final durable write on a deliberate session swap.
   */
  stop(): void {
    if (this.intervalHandle != null) {
      this.host.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    if (this.listening) {
      const remove = this.host.removeEventListener?.bind(this.host);
      if (remove) {
        remove('visibilitychange', this.onVisibility);
        for (const evt of HIDE_EVENTS) remove(evt, this.onHide);
      }
      this.listening = false;
    }
  }

  /** True while the heartbeat is active (test introspection). */
  get isRunning(): boolean {
    return this.intervalHandle != null;
  }
}

/**
 * The default browser host: real timers + window events + document.visibilityState.
 * In a non-DOM environment (tests that forget to inject a host, SSR) the event/
 * visibility hooks are simply absent — the periodic timer still works.
 */
function defaultHost(): AutosaveHost {
  const win = typeof window !== 'undefined' ? window : undefined;
  const doc = typeof document !== 'undefined' ? document : undefined;
  return {
    setInterval: (handler, ms) => setInterval(handler, ms),
    clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
    ...(win
      ? {
          addEventListener: (type, handler) => win.addEventListener(type, handler),
          removeEventListener: (type, handler) => win.removeEventListener(type, handler),
        }
      : {}),
    ...(doc ? { visibilityState: () => doc.visibilityState } : {}),
  };
}
